import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  ClipboardList,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  RefreshCw,
  Clock,
  User,
  FileText,
  ChevronRight,
  MoreVertical,
  MessageSquare,
  Tag,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Clock },
  in_review: { label: 'In Review', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: Eye },
  approved: { label: 'Approved', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500/10 text-red-400 border-red-500/30', icon: XCircle },
  reclassified: { label: 'Reclassified', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Tag },
};

const ITEM_TYPES = [
  { value: 'parser_output', label: 'Parser Output' },
  { value: 'low_confidence', label: 'Low Confidence' },
  { value: 'method_exception', label: 'Method Exception' },
  { value: 'output_validation', label: 'Output Validation' },
  { value: 'flagged', label: 'Flagged' },
];

const ReviewItemCard = ({ item, onReview }) => {
  const StatusIcon = STATUS_CONFIG[item.status]?.icon || Clock;
  const confidenceColor = 
    item.confidence_level >= 0.8 ? 'text-emerald-400' :
    item.confidence_level >= 0.5 ? 'text-amber-400' : 'text-red-400';

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all cursor-pointer"
      onClick={() => onReview(item)}
      data-testid={`review-item-${item.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            item.status === 'pending' ? 'bg-amber-500/20' :
            item.status === 'approved' ? 'bg-emerald-500/20' :
            item.status === 'rejected' ? 'bg-red-500/20' : 'bg-slate-700'
          }`}>
            <StatusIcon className={
              item.status === 'pending' ? 'text-amber-400' :
              item.status === 'approved' ? 'text-emerald-400' :
              item.status === 'rejected' ? 'text-red-400' : 'text-slate-400'
            } size={20} />
          </div>
          <div>
            <p className="text-xs text-slate-500 capitalize">{item.item_type?.replace('_', ' ')}</p>
            <h3 className="font-medium text-white">{item.source_type || 'Review Item'}</h3>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG[item.status]?.color}`}>
          {STATUS_CONFIG[item.status]?.label || item.status}
        </span>
      </div>

      {/* Issue Flag */}
      {item.issue_flag && (
        <div className="flex items-center gap-2 mb-3 text-amber-400 text-sm">
          <AlertTriangle size={14} />
          <span>{item.issue_flag}</span>
        </div>
      )}

      {/* Confidence */}
      {item.confidence_level !== null && item.confidence_level !== undefined && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-slate-500">Confidence</span>
            <span className={confidenceColor}>{(item.confidence_level * 100).toFixed(0)}%</span>
          </div>
          <div className="h-1.5 bg-stone-800 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all ${
                item.confidence_level >= 0.8 ? 'bg-emerald-500' :
                item.confidence_level >= 0.5 ? 'bg-amber-500' : 'bg-red-500'
              }`}
              style={{ width: `${item.confidence_level * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Provider */}
      {item.provider && (
        <div className="flex items-center gap-2 mb-3">
          <FileText size={14} className="text-slate-500" />
          <span className="text-sm text-slate-400 capitalize">{item.provider}</span>
        </div>
      )}

      {/* Suggested Classification */}
      {item.suggested_classification && (
        <div className="flex items-center gap-2 mb-3">
          <Tag size={14} className="text-amber-400" />
          <span className="text-sm text-slate-300">{item.suggested_classification}</span>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-800">
        <span className="text-xs text-slate-500">
          {new Date(item.created_at).toLocaleDateString()}
        </span>
        <ChevronRight size={16} className="text-slate-600" />
      </div>
    </div>
  );
};

const ReviewDetailModal = ({ item, onClose, onUpdate }) => {
  const [notes, setNotes] = useState(item.reviewer_notes || '');
  const [newClassification, setNewClassification] = useState(item.suggested_classification || '');
  const [saving, setSaving] = useState(false);

  const handleAction = async (action) => {
    setSaving(true);
    try {
      const updates = {
        status: action,
        reviewer_notes: notes,
      };
      if (action === 'reclassified' && newClassification) {
        updates.correction_applied = { new_classification: newClassification };
      }
      await onUpdate(item.id, updates);
      toast.success(`Item ${action}`);
      onClose();
    } catch (error) {
      toast.error('Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-3xl bg-stone-900 border-stone-800 text-white max-h-[90vh]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ClipboardList className="text-amber-400" size={20} />
          Review Item
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          {item.item_type?.replace('_', ' ')} - {item.source_type || 'Unknown Source'}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 max-h-[55vh] overflow-y-auto py-4">
        {/* Status & Metadata */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-stone-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Status</p>
            <span className={`px-2 py-1 text-sm rounded ${STATUS_CONFIG[item.status]?.color}`}>
              {STATUS_CONFIG[item.status]?.label}
            </span>
          </div>
          <div className="bg-stone-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Confidence</p>
            <p className="text-lg font-semibold text-white">
              {item.confidence_level !== null ? `${(item.confidence_level * 100).toFixed(0)}%` : 'N/A'}
            </p>
          </div>
          <div className="bg-stone-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Provider</p>
            <p className="text-sm text-white capitalize">{item.provider || 'N/A'}</p>
          </div>
        </div>

        {/* Issue Flag */}
        {item.issue_flag && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-amber-400">
              <AlertTriangle size={16} />
              <span className="font-medium">Issue Flag</span>
            </div>
            <p className="text-sm text-slate-300 mt-1">{item.issue_flag}</p>
          </div>
        )}

        {/* Raw Data */}
        {Object.keys(item.raw_data || {}).length > 0 && (
          <div>
            <p className="text-sm text-slate-400 mb-2">Raw Data</p>
            <pre className="bg-stone-950 rounded-lg p-3 text-xs text-slate-300 overflow-x-auto max-h-40">
              {JSON.stringify(item.raw_data, null, 2)}
            </pre>
          </div>
        )}

        {/* Extracted Data */}
        {Object.keys(item.extracted_data || {}).length > 0 && (
          <div>
            <p className="text-sm text-slate-400 mb-2">Extracted Data</p>
            <pre className="bg-stone-950 rounded-lg p-3 text-xs text-slate-300 overflow-x-auto max-h-40">
              {JSON.stringify(item.extracted_data, null, 2)}
            </pre>
          </div>
        )}

        {/* Suggested Classification */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Classification</label>
          <Input
            value={newClassification}
            onChange={(e) => setNewClassification(e.target.value)}
            placeholder="Enter classification..."
            className="bg-stone-800 border-slate-700 text-white"
          />
        </div>

        {/* Reviewer Notes */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Reviewer Notes</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this review..."
            className="bg-stone-800 border-slate-700 text-white resize-none"
            rows={3}
          />
        </div>
      </div>

      <DialogFooter className="flex justify-between">
        <Button variant="outline" onClick={onClose}>Close</Button>
        <div className="flex gap-2">
          <Button
            variant="destructive"
            onClick={() => handleAction('rejected')}
            disabled={saving}
          >
            <XCircle size={16} className="mr-2" />
            Reject
          </Button>
          <Button
            variant="outline"
            onClick={() => handleAction('reclassified')}
            disabled={saving}
          >
            <Tag size={16} className="mr-2" />
            Reclassify
          </Button>
          <Button
            onClick={() => handleAction('approved')}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <CheckCircle size={16} className="mr-2" />
            Approve
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};

const ReviewQueuePage = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');
  const [reviewingItem, setReviewingItem] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [total, setTotal] = useState(0);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.item_type = typeFilter;
      const data = await adminOpsService.listReviewQueue(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStatusCounts(data.status_counts || {});
    } catch (error) {
      toast.error('Failed to load review queue');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter, typeFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleUpdate = async (itemId, updates) => {
    adminOpsService.setAuthHeaders(getAuthHeaders());
    await adminOpsService.updateReviewItem(itemId, updates);
    fetchItems();
  };

  const filteredItems = items.filter(item =>
    item.source_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.issue_flag?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.suggested_classification?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="review-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <ClipboardList className="text-amber-400" size={28} />
            Review Queue
          </h1>
          <p className="text-slate-500 mt-1">
            Review and validate AI outputs, parser results, and flagged items
          </p>
        </div>
        <Button variant="outline" onClick={fetchItems}>
          <RefreshCw size={18} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: 'pending', label: 'Pending', count: statusCounts.pending || 0 },
          { value: 'in_review', label: 'In Review', count: statusCounts.in_review || 0 },
          { value: 'approved', label: 'Approved', count: statusCounts.approved || 0 },
          { value: 'rejected', label: 'Rejected', count: statusCounts.rejected || 0 },
          { value: 'reclassified', label: 'Reclassified', count: statusCounts.reclassified || 0 },
          { value: 'all', label: 'All', count: total },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              statusFilter === tab.value
                ? 'bg-amber-600 text-white'
                : 'bg-stone-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {tab.label}
            <span className={`px-1.5 py-0.5 rounded text-xs ${
              statusFilter === tab.value ? 'bg-white/20' : 'bg-slate-700'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search items..."
            className="pl-10 bg-stone-900 border-stone-800 text-white placeholder:text-slate-500"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44 bg-stone-900 border-stone-800 text-white">
            <Filter size={16} className="mr-2 text-slate-500" />
            <SelectValue placeholder="Item Type" />
          </SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Types</SelectItem>
            {ITEM_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Items Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48 bg-stone-800" />)}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 bg-stone-900 rounded-xl border border-stone-800">
          <ClipboardList className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white mb-2">No Items in Queue</h3>
          <p className="text-slate-500">
            {statusFilter === 'pending' 
              ? 'All items have been reviewed. Great work!'
              : 'No items match your current filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map(item => (
            <ReviewItemCard
              key={item.id}
              item={item}
              onReview={setReviewingItem}
            />
          ))}
        </div>
      )}

      {/* Review Detail Modal */}
      <Dialog open={!!reviewingItem} onOpenChange={(open) => { if (!open) setReviewingItem(null); }}>
        {reviewingItem && (
          <ReviewDetailModal
            item={reviewingItem}
            onClose={() => setReviewingItem(null)}
            onUpdate={handleUpdate}
          />
        )}
      </Dialog>
    </div>
  );
};

export default ReviewQueuePage;
